import { ref, onMounted, onUnmounted } from 'vue';

interface PodiumPlayer {
  position: number;
  score: number;
}

/**
 * Composable for animating podium reveals and score count-ups
 * Reveals podiums in order: 3rd → 2nd → 1st with staggered timing
 */
export function usePodiumAnimation(players: PodiumPlayer[]) {
  const podiumsVisible = ref<Set<number>>(new Set());
  const animatedScores = ref<Map<number, number>>(new Map());
  const allRevealed = ref(false);

  let animationFrames: number[] = [];
  let timeouts: number[] = [];

  // Easing function for smooth score animation
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  // Animate score count-up for a specific position
  const animateScore = (position: number, targetScore: number, startTime: number) => {
    const SCORE_ANIMATION_DURATION = 1000; // 1 second

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / SCORE_ANIMATION_DURATION, 1);
      const easedProgress = easeOutCubic(progress);

      const currentScore = Math.round(targetScore * easedProgress);
      animatedScores.value.set(position, currentScore);

      if (progress < 1) {
        const frameId = requestAnimationFrame(animate);
        animationFrames.push(frameId);
      }
    };

    const frameId = requestAnimationFrame(animate);
    animationFrames.push(frameId);
  };

  // Reveal a podium at a specific position
  const revealPodium = (position: number, delay: number) => {
    const timeoutId = window.setTimeout(() => {
      podiumsVisible.value.add(position);
      const player = players.find((p) => p.position === position);
      if (player) {
        animatedScores.value.set(position, 0);
        animateScore(position, player.score, performance.now());
      }
    }, delay);
    timeouts.push(timeoutId);
  };

  // Start the podium reveal sequence
  const startAnimation = () => {
    // Reveal order: 3rd (1000ms) → 2nd (2500ms) → 1st (4000ms)
    const delays = {
      3: 500,
      2: 2000,
      1: 3500,
    };

    players.forEach((player) => {
      if (player.position <= 3) {
        revealPodium(player.position, delays[player.position as keyof typeof delays]);
      }
    });

    // Mark all as revealed after final podium + animation completes
    const finalDelay = 5000;
    const timeoutId = window.setTimeout(() => {
      allRevealed.value = true;
    }, finalDelay);
    timeouts.push(timeoutId);
  };

  // Cleanup
  const cleanup = () => {
    animationFrames.forEach((id) => cancelAnimationFrame(id));
    timeouts.forEach((id) => clearTimeout(id));
    animationFrames = [];
    timeouts = [];
  };

  onMounted(() => {
    startAnimation();
  });

  onUnmounted(() => {
    cleanup();
  });

  return {
    podiumsVisible,
    animatedScores,
    allRevealed,
  };
}
